import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import CustomButton from "../Common/CustomButton";
import { TagsInputUniqueKeys } from "../Common/TagInputsUniqueKeys";
import { AgreementStatus, VendorData } from "./VendorDashboard";

export type VendorFormData = Partial<VendorData>;

type AddEditVendorProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: VendorFormData) => void;
  initialData?: VendorData;
  anchorOptions: { anchorId: number; anchorName: string }[];
};

const defaultFormData: VendorFormData = {
  pan: "",
  displayName: "",
  discountRate: null,
  holdBack: null,
  agreementStatus: null,
  creditPeriod: null,
  useInvoiceCreditPeriod: false,
  registeredName: "",
  cin: "",
  address: "",
  gstin: "",
  authorisedSignatoryName: "",
  authorisedSignatoryDesignation: "",
  authorisedSignatoryPhoneNumber: "",
  anchorId: null,
};

export const AddEditVendor: React.FC<AddEditVendorProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  anchorOptions,
}) => {
  const [formData, setFormData] = React.useState<VendorFormData>(defaultFormData);
  const [authorisedSignatoryEmailId, setAuthorisedSignatoryEmailId] = React.useState<string[]>(
    initialData?.authorisedSignatoryEmailId || []
  );
  const [emailIdToSendCN, setEmailIdToSendCN] = React.useState<string[]>(initialData?.emailIdToSendCN || []);
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const [isSavingVendor, setIsSavingVendor] = React.useState(false);

  useEffect(() => {
    setFormData(initialData ? { ...defaultFormData, ...initialData } : defaultFormData);
    setErrors({});
  }, []);

  const handleChange = (field: keyof VendorData, value: string | boolean | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.pan) newErrors.pan = "PAN is required";
    if (!formData.displayName) newErrors.displayName = "Vendor Name is required";
    if (!formData.anchorId) newErrors.anchorId = "Anchor is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSavingVendor(true);
    const cleanedObject = Object.fromEntries(
      Object.entries(formData).filter(([_, value]) => value !== "" && value !== null)
    );
    cleanedObject.authorisedSignatoryEmailId = authorisedSignatoryEmailId.filter((email) => email.trim() !== "");
    cleanedObject.emailIdToSendCN = emailIdToSendCN.filter((email) => email.trim() !== "");
    await onSubmit(cleanedObject);
    setIsSavingVendor(false);
  };

  const renderField = (
    label: string,
    field: keyof VendorData,
    props: Partial<React.ComponentProps<typeof TextField>> = {}
  ) => (
    <Stack flex={1}>
      <Typography variant="subtitle2" fontWeight={500}>
        {label}
      </Typography>
      <TextField
        value={formData[field] ?? ""}
        onChange={(e) => handleChange(field, e.target.value)}
        fullWidth
        size="small"
        error={!!errors[field]}
        helperText={errors[field]}
        {...props}
      />
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {initialData ? "Edit Vendor" : "Add Vendor"}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box display="flex" gap={2}>
            {renderField("PAN *", "pan", { disabled: !!initialData })}
            {renderField("Vendor Name *", "displayName")}
          </Box>
          <Box display="flex" gap={2}>
            <Stack flex={1}>
              <Typography variant="body2" fontWeight={500}>
                Anchor *
              </Typography>
              <Autocomplete
                value={anchorOptions.find((option) => option.anchorId === formData.anchorId) || null}
                options={anchorOptions}
                getOptionLabel={(option) => option.anchorName}
                isOptionEqualToValue={(option, value) => option.anchorId === value.anchorId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    error={!!errors.anchorId}
                    helperText={errors.anchorId}
                  />
                )}
                onChange={(_event, value) => {
                  handleChange("anchorId", value?.anchorId);
                }}
              />
            </Stack>
          </Box>

          <Box display="flex" gap={2}>
            <Stack flex={1}>
              <Typography variant="body2" fontWeight={500}>
                Discount rate
              </Typography>
              <TextField
                type="number"
                value={formData.discountRate ?? ""}
                onChange={(e) => handleChange("discountRate", Number(e.target.value))}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">% </InputAdornment>,
                }}
              />
            </Stack>

            <Stack flex={1}>
              <Typography variant="body2" fontWeight={500}>
                Holdback
              </Typography>
              <TextField
                type="number"
                value={formData.holdBack ?? ""}
                onChange={(e) => handleChange("holdBack", Number(e.target.value))}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">% </InputAdornment>,
                }}
              />
            </Stack>
          </Box>

          <Box display="flex" gap={2}>
            <Box flex={1}>
              <Typography variant="subtitle2">Email IDs to send Credit Note</Typography>
              <Box>
                <TagsInputUniqueKeys
                  fullWidth={true}
                  popupIcon={null}
                  open={false}
                  tags={emailIdToSendCN}
                  setTags={setEmailIdToSendCN}
                  placeholder="Newline separated Email IDs to send Credit Note"
                  splitter="newline"
                  limitTags={1}
                  textFieldProps={{ multiline: true, maxRows: 1 }}
                  options={[]}
                  accessor={"emailIdToSendCN"}
                  uniqueKeyAccessor={"emailIdToSendCN"}
                />
              </Box>
            </Box>
            <Stack flex={1}>
              <Typography variant="body2" fontWeight={500}>
                Status of agreement
              </Typography>
              <TextField
                select
                value={formData.agreementStatus ?? ""}
                onChange={(e) => handleChange("agreementStatus", e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value={AgreementStatus.SENT}>Sent</MenuItem>
                <MenuItem value={AgreementStatus.SIGNED}>Signed</MenuItem>
                <MenuItem value={AgreementStatus.NOT_SENT}>Not Sent</MenuItem>
              </TextField>
            </Stack>
          </Box>

          <Box display="flex" gap={2}>
            <Stack flex={1}>
              <Typography variant="body2" fontWeight={500}>
                Actual Credit Period (In Days)
              </Typography>
              <TextField
                value={formData.creditPeriod ?? ""}
                onChange={(e) => handleChange("creditPeriod", Number(e.target.value))}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack flex={1}>
              <Typography variant="body2" fontWeight={500}>
                Use the previous credit period in calculations
              </Typography>
              <TextField
                select
                value={formData.useInvoiceCreditPeriod ? "Yes" : "No"}
                onChange={(e) => handleChange("useInvoiceCreditPeriod", e.target.value === "Yes")}
                fullWidth
                size="small"
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </Stack>
          </Box>

          <Box display="flex" gap={2}>
            {renderField("Registered Name of the company", "registeredName")}
            {renderField("CIN", "cin")}
          </Box>

          <Box display="flex" gap={2}>
            {renderField("Address", "address", {
              multiline: true,
              inputProps: { maxLength: 500 },
            })}
            {renderField("GSTIN", "gstin")}
          </Box>

          <Box display="flex" gap={2}>
            {renderField("Authorised signatory name", "authorisedSignatoryName")}
            {renderField("Authorised signatory designation", "authorisedSignatoryDesignation")}
          </Box>

          <Box display="flex" gap={2}>
            <Box flex={1}>
              <Typography variant="subtitle2">Authorised Signatory Email IDs</Typography>
              <Box>
                <TagsInputUniqueKeys
                  popupIcon={null}
                  open={false}
                  tags={authorisedSignatoryEmailId}
                  setTags={setAuthorisedSignatoryEmailId}
                  fullWidth={true}
                  placeholder="Newline separated Authorised Signatory Email IDs"
                  splitter="newline"
                  limitTags={1}
                  textFieldProps={{ multiline: true, maxRows: 1 }}
                  options={[]}
                  accessor={"authorisedSignatoryEmailId"}
                  uniqueKeyAccessor={"authorisedSignatoryEmailId"}
                />
              </Box>
            </Box>
            {renderField("Authorised signatory phone number", "authorisedSignatoryPhoneNumber", { type: "tel" })}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <CustomButton variant="text" onClick={onClose}>
          Cancel
        </CustomButton>
        <CustomButton
          onClick={handleSubmit}
          variant="contained"
          disabled={isSavingVendor}
          startIcon={isSavingVendor ? <CircularProgress size={20} /> : null}
        >
          {initialData ? "Update Vendor" : "Add Vendor"}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
